'use client';

import { cva } from 'class-variance-authority';
import { Tweet as TweetComponent } from '@/components/tweet';
import type { TweetProperties } from '@/components/tweet/Tweet';
import { useTweetById } from '@/domains/timeline/components/InlineTweet';
import { TweetNotFound } from '@/domains/timeline/components/TweetNotFound';
import { TweetSkeleton } from '@/domains/timeline/components/TweetSkeleton';
import { cn } from '@/lib/utils';
import { getRecordValue, isRecord } from '@/lib/utils/type-guards';

interface ChatTweetProperties {
  className?: string;
  disableSpacing?: boolean;
  tweetId: string;
}

const containerVariants = cva('', {
  variants: {
    spacing: {
      default: 'my-4',
      none: '',
    },
  },
  defaultVariants: {
    spacing: 'default',
  },
});

export function ChatTweet({ tweetId, className, disableSpacing = false }: ChatTweetProperties) {
  const { data: tweet, isLoading, isError } = useTweetById(tweetId);
  const renderableTweet = isRenderableTweet(tweet) ? tweet : null;
  const spacing = disableSpacing ? 'none' : 'default';

  if (isLoading) {
    return (
      <div className={cn(containerVariants({ spacing }), className)}>
        <TweetSkeleton variant="card" />
      </div>
    );
  }

  if (isError || !renderableTweet) {
    return (
      <div className={cn(containerVariants({ spacing }), className)}>
        <TweetNotFound tweetId={tweetId} />
      </div>
    );
  }

  return (
    <div className={cn(containerVariants({ spacing }), className)}>
      <TweetComponent
        tweet={renderableTweet}
        disableHover
        disableConversationTrigger
        className="rounded-2xl border border-border/60 bg-card shadow-sm"
      />
    </div>
  );
}

function isRenderableTweet(candidate: unknown): candidate is TweetProperties['tweet'] {
  if (!isRecord(candidate)) {
    return false;
  }
  const id = getRecordValue(candidate, 'id');
  const text = getRecordValue(candidate, 'text');
  const createdAt = getRecordValue(candidate, 'createdAt');
  const authorId = getRecordValue(candidate, 'authorId');
  const likeCount = getRecordValue(candidate, 'likeCount');
  const retweetCount = getRecordValue(candidate, 'retweetCount');
  const replyCount = getRecordValue(candidate, 'replyCount');
  const quoteCount = getRecordValue(candidate, 'quoteCount');
  return (
    typeof id === 'string' &&
    typeof text === 'string' &&
    createdAt instanceof Date &&
    typeof authorId === 'string' &&
    typeof likeCount === 'number' &&
    typeof retweetCount === 'number' &&
    typeof replyCount === 'number' &&
    typeof quoteCount === 'number'
  );
}
